<template>
  <div class="user">
    <v-card>
      <v-toolbar color="secondary lighten-2" dark>
        <v-toolbar-title>
          {{ $t("user_profile") }} -
          {{ currentUser.displayName }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card-text>
        <v-container fluid>
          <v-layout row wrap>
            <v-flex d-flex xs1 v-if="config.dialog.avatar">
              <v-btn flat v-if="uploadAvatarHandle" :disabled="!getIsOnline">
                <v-avatar @click="updateAvatar()">
                  <img :src="uploadAvatarHandle.url" :alt="$t('user_avatar')" />
                </v-avatar>
              </v-btn>

              <v-btn flat v-else-if="currentUser.avatar" :disabled="!getIsOnline">
                <v-avatar @click="updateAvatar()">
                  <img :src="currentUser.avatar" :alt="$t('user_current_avatar')" />
                </v-avatar>
              </v-btn>

              <v-btn flat v-else>
                <v-avatar @click="updateAvatar()" :disabled="!getIsOnline">
                  <v-icon>person</v-icon>
                </v-avatar>
              </v-btn>
            </v-flex>

            <v-flex xs11 v-if="config.dialog.name">
              <v-text-field
                :label="$t('user_fullname_lb')"
                v-model="currentUser.displayName"
                required
              />
            </v-flex>
          </v-layout>

          <v-text-field
            :disabled="isUpdate && presentEmail"
            :label="$t('user_email_lb')"
            v-model="currentUser.email"
            required
            v-if="config.dialog.email"
          />

          <v-container fluid v-if="config.dialog.groups">
            <v-flex>
              <v-card>
                <v-card-title primary-title>
                  <div>
                    <h3 class="headline mb-0">{{ $t("user_roles_lb") }}</h3>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="currentUser.firebase_id"
                    color="primary"
                    outline
                    disabled
                  >{{ $t("user_login_active") }}</v-btn>
                  <v-btn
                    v-else-if="isUpdate"
                    color="primary"
                    @click="linkFirebase()"
                  >{{ $t("user_login_inactive") }}</v-btn>
                </v-card-title>
                <v-card-text>
                  <v-checkbox
                    :disabled="activeLogin === false"
                    v-for="(role, role_index) in roles"
                    :key="role_index"
                    :label="role.description"
                    v-model="role.member"
                  ></v-checkbox>
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex class="text-xs-center" mt-5></v-flex>
          </v-container>

          <v-text-field
            v-if="config.dialog.type"
            :label="$t('user_type')"
            v-model="currentUser.type"
            hint="example of persistent helper text"
            persistent-hint
          />

          <v-flex class="text-xs-center" mt-5 v-if="config.dialog.googlelogin">
            <v-btn color="primary" @click="userLinkWithGoogle()">{{ $t("user_link_google_btn") }}</v-btn>
            <v-btn
              color="secondary"
              @click="userUnlinkWithGoogle()"
            >{{ $t("user_unlink_google_btn") }}</v-btn>
          </v-flex>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-btn class="blue--text darken-1" flat @click.native="close()">{{ $t("close") }}</v-btn>
        <v-btn class="blue--text darken-1" flat @click.native="save()">{{ $t("save") }}</v-btn>
      </v-card-actions>
    </v-card>

    <input
      type="file"
      style="display:none;"
      ref="image"
      accept="image/*"
      @change="onFilePickedCached"
    />
  </div>
</template>
<i18n>
{
  "de" : {
    "user_profile" : "Profil",
    "user_avatar" : "Avatar",
    "user_current_avatar" : "Aktueller Avatar",
    "user_fullname_lb" : "Vor- und Nachname",
    "user_email_lb" : "Email",
    "user_roles_lb" : "Rollen",
    "user_type_lb" : "Nutzertyp",
    "user_type" : "Typ",
    "user_link_google_btn" : "Mit Google-Konto verbinden",
    "user_unlink_google_btn" : "Von Google-Konto trennen",
    "close" : "Schließen",
    "save" : "Speichern",
    "user_roles_user" : "Benutzer",
    "user_roles_auditor" : "Auditor",
    "user_roles_qm_employee" : "QM-Mitarbeiter",
    "user_roles_admin" : "Administrator",
    "user_login_active" : "Login aktiv",
    "user_login_inactive" : "Login aktivieren"
  },
  "en" : {
    "user_profile" : "Profil",
    "user_avatar" : "Avatar",
    "user_current_avatar" : "Current Avatar",
    "user_fullname_lb" : "Full Name",
    "user_email_lb" : "Email",
    "user_roles_lb" : "Roles",
    "user_type_lb" : "User type",
    "user_type" : "Type",
    "user_link_google_btn" : "Connect Account with Google",
    "user_unlink_google_btn" : "Unlink Google Account",
    "close" : "Close",
    "save" : "Save",
    "user_roles_user" : "User",
    "user_roles_auditor" : "Auditor",
    "user_roles_qm_employee" : "QM Employee",
    "user_roles_admin" : "Administrator",
    "user_login_active" : "Login active",
    "user_login_inactive" : "Activate login"

  }
}
</i18n>
<script>
import uuid from "uuid";
import firebase from "firebase";

// Mixins
import FileUploaderMixin from "@/components/mixins/FileUploaderMixin.vue";

import { mapGetters, mapActions } from "vuex";
import { api } from "@/store/modules/users";
import { api as confApi } from "@/store/modules/configuration";
import { api as appApi } from "@/store/modules/app";
import { uploadFile } from "@/utils/uploader";

export default {
  props: {
    userId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      currentUser: {},
      presentEmail: true,
      roles: [
        {
          id: "user",
          description: this.$i18n.t("user_roles_user"),
          member: true
        },
        {
          id: "auditor",
          description: this.$i18n.t("user_roles_auditor"),
          member: false
        },
        {
          id: "qm",
          description: this.$i18n.t("user_roles_qm_employee"),
          member: false
        },
        {
          id: "admin",
          description: this.$i18n.t("user_roles_admin"),
          member: false
        }
      ]
    };
  },
  mixins: [FileUploaderMixin],
  async mounted() {
    console.log("Rollen", this.currentRoles);
    this.currentUser = this.cCurrentUser;
    if (this.currentRoles) {
      this.roles = this.currentRoles;
    }
    if (!this.currentUser.email) {
      this.presentEmail = false;
    }
  },
  computed: {
    ...mapGetters(api.namespace, [
      api.getters.getUser,
      api.getters.getRolesByUser
    ]),
    ...mapGetters(appApi.namespace, [appApi.getters.getIsOnline]),
    cCurrentUser() {
      if (this.userId) {
        return this.getUser(this.userId);
      } else {
        return {};
      }
    },
    activeLogin() {
      return this.currentUser.firebase_id ? true : false;
    },
    isUpdate() {
      if (this.userId) {
        return true;
      } else {
        return false;
      }
    },
    ...mapGetters(confApi.namespace, [confApi.getters.user]),
    config() {
      return this.user || {};
    },
    currentRoles() {
      let x = this.getRolesByUser(this.currentUser.firebase_id);
      console.log("Rollen laden", x);
      return x;
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.updateUser,
      api.actions.createUser,
      api.actions.updateRoles,
      api.actions.enableProviderFirebase,
      api.actions.disableProviderFirebase
    ]),
    ...mapActions(appApi.namespace, [appApi.actions.setStatus]),
    async linkFirebase() {
      const payload = {
        user_id: this.currentUser.id,
        data: this.currentUser
      };

      this.enableProviderFirebase(payload).then(() => {
        console.log("WHHHYYYYYY");
        this.$nextTick().then(() => {
          console.log("y", this.currentUser);
        });
      });
    },
    async unlinkFirebase() {
      const payload = {
        user_id: this.currentUser.id,
        data: this.currentUser
      };

      this.disableProviderFirebase(payload);
    },
    async setRoles(userId) {
      // TODO: What about the userId prop? How can we asure, that it is a firebase_id?

      let user_id = "";

      if (userId) {
        user_id = userId;
      } else {
        user_id = this.currentUser.firebase_id;
      }

      console.log("Der Nutzer", this.currentUser, user_id);
      if (!user_id) {
        return Promise.reject(
          "User has no firebase_id and can not be assigned to roles"
        );
      }

      let payload = { data: this.roles, user_id: user_id };

      console.log("UserRoles Step1", this.roles);
      await this.updateRoles(payload);
    },
    updateAvatar(user_id) {
      console.log("updateAvatar", user_id, this.getIsOnline);
      if (this.getIsOnline) {
        // TODO: Muss hier sinnvollerweise eine User-ID vergeben werden?
        this.$refs.image.click();
      }
    },
    async userLinkWithGoogle() {
      let provider = new firebase.auth.GoogleAuthProvider();

      let change = await firebase
        .auth()
        .currentUser.linkWithPopup(provider)
        .then(function(result) {
          // Accounts successfully linked.
          console.log("LINKED GOOGLE ACCOUNT", result);
        })
        .catch(function(error) {
          console.log("LINKED GOOGLE ACCOUNT FAILED", error);

          // Get reference to the currently signed-in user
          let prevUser = firebase.auth().currentUser;
          // Sign in user with another account
          firebase
            .auth()
            .signInWithPopup(new firebase.auth.GoogleAuthProvider())
            .then(function(user) {
              console.log("Sign In Success", user);

              firebase
                .auth()
                .currentUser.unlink("google.com")
                .then(function(result) {
                  // Accounts successfully linked.
                  console.log("UNLINKED GOOGLE", result);
                  return prevUser.linkWithCredential(user.credential);
                })
                .then(function() {})
                .catch(function(error) {
                  console.log("UNLINKED GOOGLE FAILED", error);
                  // Handle Errors here.
                  // ...
                });
            });
        });

      console.log("Wsa auch immer das ist", change);
    },
    async userUnlinkWithGoogle() {
      let x = await firebase.auth().currentUser.providerData;

      console.log("Prov", x);

      firebase
        .auth()
        .currentUser.unlink("google.com")
        .then(function(result) {
          // Accounts successfully linked.
          console.log("UNLINKED GOOGLE", result);
          // ...
        })
        .catch(function(error) {
          console.log("UNLINKED GOOGLE FAILED", error);
          // Handle Errors here.
          // ...
        });
    },
    save() {
      let user = { ...this.currentUser };

      console.log("Hier gehtś rund");

      if (this.uploadAvatarHandle) {
        console.log(this.uploadAvatarHandle);

        const imageFile = this.uploadAvatarHandle.file;

        uploadFile(imageFile, {
          userId: this.currentUser.id
        })
          .then(uploadSnapshot => {
            return uploadSnapshot.ref.getDownloadURL();
          })
          .then(downloadUrl => {
            console.log("Download URL", downloadUrl);

            user = { ...this.currentUser, avatar: downloadUrl };

            const payload = {
              data: user,
              user_id: this.currentUser.id,
              activity: {
                id: uuid.v4(),
                avatar: this.$user.avatar(),
                actor: this.$user.id(),
                actor_name: this.$user.displayName(),
                verb:
                  this.$user.displayName() +
                  " hat den Nutzer " +
                  this.currentUser.displayName +
                  " bearbeitet",
                object: this.currentUser.id,
                timestamp: new Date()
              }
            };

            if (this.isUpdate) {
              this.updateUser(payload).then(userId => {
                console.log("User ist geupdated", userId);
                this.setRoles();
                this.close();
              });
            } else {
              this.createUser(payload).then(userId => {
                console.log("User ist erstellt", userId);
                this.setRoles(userId);
                this.close();
              });
            }
          })
          .catch(error => {
            console.log("Fehler", error);
          });
      } else {
        const payload = {
          data: user,
          user_id: this.currentUser.id,
          activity: {
            id: uuid.v4(),
            avatar: this.$user.avatar(),
            actor: this.$user.id(),
            actor_name: this.$user.displayName(),
            verb:
              this.$user.displayName() +
              " hat den Nutzer " +
              this.currentUser.displayName +
              " bearbeitet",
            object: this.currentUser.id,
            timestamp: new Date()
          }
        };

        if (this.isUpdate) {
          this.updateUser(payload).then(userId => {
            console.log("User ist geupdated", userId);
            this.setRoles();
            this.close();
          });
        } else {
          this.createUser(payload)
            .then(userId => {
              console.log("User ist erstellt", userId);
              this.setRoles(userId).then(() => {
                this.close();
              });
            })
            .catch(error => {
              console.log("Fehler ist aufgetreten", error);
            });
        }
      }
    },
    close() {
      console.log("close");
      this.$router.go("-1");
    }
  },
  watch: {
    cCurrentUser(val) {
      console.log("Watcher CCU", val);
      //this.currentUser(val)
      this.currentUser = val;
    },
    async currentUser(val) {
      console.log("Watcher CU", val);
      await this.$nextTick();
    },
    currentRoles(val) {
      console.log("Role hat sich verändert", val);
      //this.roles = this.currentRoles
    }
  }
};
</script>
